import { type ReactNode, useEffect, useId, useRef } from 'react';

export interface DialogProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the dialog requests to close (backdrop click or Escape key) */
  onClose: () => void;
  /** Dialog title — rendered in the header and linked via aria-labelledby */
  title: string;
  /** Dialog content */
  children: ReactNode;
  /** Optional extra class for the inner panel (width, max-height overrides, etc.) */
  className?: string;
}

/**
 * Accessible, reusable dialog overlay.
 *
 * - role="dialog" + aria-modal + aria-labelledby
 * - Backdrop click closes
 * - Escape key closes
 * - Focuses the panel on open (basic focus trap entry point)
 *
 * Usage:
 * ```tsx
 * <Dialog open={isOpen} onClose={() => setOpen(false)} title="Mon titre">
 *   <p>Contenu libre</p>
 * </Dialog>
 * ```
 */
export default function Dialog({ open, onClose, title, children, className }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Focus the panel when dialog opens
  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' aria-hidden='true' onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative z-10 w-full bg-white rounded-2xl shadow-2xl focus:outline-none p-5 ${className ?? 'max-w-lg'}`}
      >
        {/* Hidden title anchor for aria-labelledby — visible title is rendered by children */}
        <span id={titleId} className='sr-only'>
          {title}
        </span>
        {children}
      </div>
    </div>
  );
}
